import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import { useState, useEffect } from 'react';
import ErrorBox from '../../core/components/ErrorBox';
import PageContainer from '../../core/components/PageContainer';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import useCurrentUser from '../../core/hooks/useCurrentUser';
import { apiRawFetch } from '../../main/client';
import InvitationsTab from './InvitationsTab';
import UserFormDialog from './UserFormDialog';

interface GlobalAdminPermission {
    type: 'globalAdmin';
}

interface PropertyAdminPermission {
    type: 'propertyAdmin';
    propertyId: number;
}

type Permission = GlobalAdminPermission | PropertyAdminPermission;

interface User {
    id: number;
    username: string;
    name: string;
    permissions: Permission[];
    created: string;
    updated: string;
}

const UsersPage = () => {
    const { user: currentUser, loading: userLoading, isGlobalAdmin } = useCurrentUser();
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [currentTab, setCurrentTab] = useState(0);

    // Route guard: redirect non-global admins to home
    useEffect(() => {
        if (!userLoading && currentUser && !isGlobalAdmin()) {
            navigate({ to: '/', replace: true });
        }
    }, [userLoading, currentUser, isGlobalAdmin, navigate]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await apiRawFetch('api/users');

            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }

            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleCreateUser = () => {
        setEditingUser(null);
        setDialogOpen(true);
    };

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setDialogOpen(true);
    };

    const handleUserSaved = () => {
        setDialogOpen(false);
        setEditingUser(null);
        fetchUsers();
    };

    // Don't render while checking permissions or if not global admin
    if (userLoading || !currentUser || !isGlobalAdmin()) {
        return (
            <PageContainer>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    User Management
                </Typography>
                {currentTab === 0 && (
                    <ResponsiveActionButton
                        label="Add User"
                        icon={<AddIcon />}
                        onClick={handleCreateUser}
                    />
                )}
            </Box>

            <Box
                sx={{
                    borderBottom: '3px solid #6366f1',
                    mb: 3,
                }}
            >
                <Tabs
                    value={currentTab}
                    onChange={(_, newValue) => setCurrentTab(newValue)}
                    sx={{
                        '& .MuiTab-root': {
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem',
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#6366f1',
                            height: 3,
                        },
                    }}
                >
                    <Tab label="Users" />
                    <Tab label="Invitations" />
                </Tabs>
            </Box>

            {currentTab === 0 && (
                <Box>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 8,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                border: '2px solid #374151',
                                borderRadius: 0,
                            }}
                        >
                            <Table aria-label="users table">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            borderBottom: '3px solid #6366f1',
                                            backgroundColor: '#1a1a2e',
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                py: 2,
                                                px: 2,
                                            }}
                                        >
                                            ID
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                px: 2,
                                            }}
                                        >
                                            Username
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                px: 2,
                                            }}
                                        >
                                            Name
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                px: 2,
                                            }}
                                        >
                                            Created
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                px: 2,
                                            }}
                                        >
                                            Updated
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                align="center"
                                                sx={{ py: 6 }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ textTransform: 'uppercase' }}
                                                >
                                                    No users found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        users.map(user => (
                                            <TableRow
                                                key={user.id}
                                                onClick={() => handleEditUser(user)}
                                                sx={{
                                                    borderBottom: '1px solid #374151',
                                                    transition: 'all 0.2s ease',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'rgba(99, 102, 241, 0.1)',
                                                        transform: 'translateX(2px)',
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ py: 2, px: 2 }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {user.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ px: 2 }}>
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={700}
                                                        sx={{ fontFamily: 'monospace' }}
                                                    >
                                                        {user.username}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ px: 2 }}>
                                                    <Typography variant="body2">
                                                        {user.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ px: 2 }}>
                                                    <Typography variant="body2">
                                                        {new Date(
                                                            user.created
                                                        ).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ px: 2 }}>
                                                    <Typography variant="body2">
                                                        {new Date(
                                                            user.updated
                                                        ).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <UserFormDialog
                        open={dialogOpen}
                        user={editingUser}
                        currentUserPermissions={currentUser?.permissions ?? []}
                        onClose={() => setDialogOpen(false)}
                        onSaved={handleUserSaved}
                        onDeleted={handleUserSaved}
                    />
                </Box>
            )}

            {currentTab === 1 && <InvitationsTab />}
        </PageContainer>
    );
};

export default UsersPage;
